import * as React from "react";
import { SimpleContainerProps } from "../SimpleContainer/typings";
import { Viewport, ViewSmall, ViewMedium, ViewLarge } from "bernie-view";
import { Column } from "components/flexComponents/MultiColumnContainer/model/Column";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { OneColumn } from "components/flexComponents/MultiColumnContainer/model/OneColumn";
import { TwoColumnInverseFallback } from "components/flexComponents/MultiColumnContainer/decorators/TwoColumn/TwoColumnInverseFallback";
import { OneToManyTwoColumnLeft } from "./decorators/TwoColumn/OneToManyTwoColumnLeft";
import { OneToManyTwoColumnRight } from "./decorators/TwoColumn/OneToManyTwoColumnRight";
import { EqualTwoColumn } from "components/flexComponents/MultiColumnContainer/decorators/TwoColumn/EqualTwoColumn";
import { FlexComponent } from "typings/flexFramework/FlexComponent";
import { BlossomComponents } from "src/components/flexFramework/BlossomComponents/BlossomComponents";

export const MultiColumnContainer = (props: SimpleContainerProps) => {
  const { flexComponent, context } = props;

  // Fast-fail
  if (!flexComponent || !flexComponent.model || flexComponent.model.mobileWidth === "hidden") {
    return null;
  }

  // Return null for regions with no title, inner title and all "empty" modules
  if (
    !flexComponent.model.title &&
    !flexComponent.model.innerTitle &&
    flexComponent.modules &&
    flexComponent.modules.length > 0 &&
    flexComponent.modules.filter(module => !module.model.empty).length === 0
  ) {
    return null;
  }

  // Collect list of modules to render
  const modulesToRender = flexComponent.modules.filter(module => !module.model.empty);
  const desktopColumn = new OneColumn(modulesToRender, flexComponent.model.width);
  const tabletColumn = new OneColumn([...modulesToRender], flexComponent.model.tabletWidth);
  const mobileColumn = new OneColumn([...modulesToRender], flexComponent.model.mobileWidth);

  const headingClass = "uitk-type-heading-600 all-t-padding-three uitk-cell all-cell-1-1 all-l-padding-zero";

  return (
    <>
      {flexComponent.model.title && <h2 className={headingClass}>{flexComponent.model.title}</h2>}
      {flexComponent.model.innerTitle && <h2 className={headingClass}>{flexComponent.model.innerTitle}</h2>}
      <Viewport>
        <ViewSmall>{renderColumns(mobileColumn.getColumnsToRender(), context)}</ViewSmall>
        <ViewMedium>{renderColumns(tabletColumn.getColumnsToRender(), context)}</ViewMedium>
        <ViewLarge>
          {renderColumns(decorateColumn(desktopColumn, flexComponent).getColumnsToRender(), context)}
        </ViewLarge>
      </Viewport>
    </>
  );
};

// TODO: Is there a prettier way to do this?
const decorateColumn = (mutableColumn: Column, flexComponent: FlexComponent) => {
  if (
    flexComponent.name === LayoutName.ONE_TO_MANY_TWO_COLUMN_LEFT ||
    flexComponent.name === LayoutName.ONE_TO_MANY_TWO_COLUMN_LEFT_INVERSE_FALLBACK
  ) {
    mutableColumn = new OneToManyTwoColumnLeft(mutableColumn);
  }

  if (
    flexComponent.name === LayoutName.ONE_TO_MANY_TWO_COLUMN_RIGHT ||
    flexComponent.name === LayoutName.ONE_TO_MANY_TWO_COLUMN_RIGHT_INVERSE_FALLBACK
  ) {
    mutableColumn = new OneToManyTwoColumnRight(mutableColumn);
  }

  if (
    flexComponent.name === LayoutName.ONE_TO_MANY_TWO_COLUMN_LEFT_INVERSE_FALLBACK ||
    flexComponent.name === LayoutName.ONE_TO_MANY_TWO_COLUMN_RIGHT_INVERSE_FALLBACK
  ) {
    mutableColumn = new TwoColumnInverseFallback(mutableColumn);
  }

  if (flexComponent.name === LayoutName.ONE_TO_MANY_TWO_COLUMN_EQUAL) {
    mutableColumn = new EqualTwoColumn(mutableColumn);
  }

  return mutableColumn;
};

/**
 * Enum values for "layout names" supported by MultiColumnContainer.
 * These values are strictly 1-1 with the region view names available on FM.
 */
enum LayoutName {
  ONE_TO_MANY_TWO_COLUMN_LEFT = "one-to-many-two-column-left",
  ONE_TO_MANY_TWO_COLUMN_RIGHT = "one-to-many-two-column-right",
  ONE_TO_MANY_TWO_COLUMN_LEFT_INVERSE_FALLBACK = "one-to-many-two-column-left-inverse-fallback",
  ONE_TO_MANY_TWO_COLUMN_RIGHT_INVERSE_FALLBACK = "one-to-many-two-column-right-inverse-fallback",
  ONE_TO_MANY_TWO_COLUMN_EQUAL = "equal-two-column"
}

function renderColumns(columns: Column[], context: ExtendedContextStore) {
  const regionWidthToGridMap = {
    "25": "all-cell-1-4",
    "33": "all-cell-1-3",
    "50": "all-cell-1-2",
    "66": "all-cell-2-3",
    "75": "all-cell-3-4",
    "100": "all-cell-1-1"
  };

  const columnsToRender = columns.map((column, index) => {
    return (
      <div
        key={index}
        className={`MultiColumnContainer uitk-flex-align-content-flex-start uitk-flex-grow-0 uitk-cell uitk-grid ${
          regionWidthToGridMap[column.width]
        }`}
      >
        <BlossomComponents flexComponents={column.modules} context={context} />
      </div>
    );
  });

  return <div className="uitk-cell uitk-grid">{columnsToRender}</div>;
}

export default MultiColumnContainer;
